import React from "react";
import { AiOutlineCreditCard, AiOutlineForm, AiOutlineUser, AiOutlineFileText } from "react-icons/ai";

export interface ServiceStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  tip?: string;
}

interface ServiceProcessProps {
  label: string;
  steps?: ServiceStep[]; // optional custom steps per program
}

const ServiceProcess: React.FC<ServiceProcessProps> = ({ label, steps }) => {

  // Default steps if none provided
  const defaultSteps: ServiceStep[] = [
    {
      id: 1,
      title: "Select & Pay",
      description: "Pay easily using payment link or QR code to start your chosen service.",
      icon: <AiOutlineCreditCard className="text-accent w-6 h-6" />,
      tip: `💡 Take the first step for your '${label}' journey.`
    },
    {
      id: 2,
      title: "Fill Application Form",
      description: `Provide your '${label}', target job info, and other details for personalized feedback.`,
      icon: <AiOutlineForm className="text-accent w-6 h-6" />,
      tip: `💡 We need to understand you well to create your best '${label}'.`
    },
    {
      id: 3,
      title: "Expert Review & Feedback",
      description: "A UK-based career expert will analyze your document 1:1 and provide actionable improvements.",
      icon: <AiOutlineUser className="text-accent w-6 h-6" />,
      tip: `💡 Receive feedback from an expert familiar with the local job market.`
    },
    {
      id: 4,
      title: "Receive Report",
      description: "Receive your revised document and detailed feedback via email within 3-5 business days.",
      icon: <AiOutlineFileText className="text-accent w-6 h-6" />,
      tip: `💡 Clear direction to take the next step beyond simple corrections.`
    },
  ];

  // Use custom steps if provided, else default steps
  const programSteps = steps || defaultSteps;

  return (
    <div className="max-w-5xl mx-auto my-12 px-4">
      <h2 className="text-3xl font-bold text-main text-center mb-12">How it works</h2>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-5 top-0 h-full border-l-2 border-gray-200"></div>

        <div className="flex flex-col space-y-12">
          {programSteps.map((step, idx) => (
            <div key={step.id} className="flex items-start space-x-6 relative">
              {/* Step Number / Icon */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center font-bold z-10">
                  {step.id}
                </div>
                {idx !== programSteps.length - 1 && <div className="flex-1 w-px bg-gray-200 mt-1"></div>}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {step.icon}
                  <h3 className="text-xl font-semibold text-main text-left">{step.title}</h3>
                </div>
                <p className="text-gray-700 mb-1 text-left">{step.description}</p>
                {step.tip && <p className="text-gray-500 text-sm text-left">{step.tip}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceProcess;
